import { AlertTriangle, ShieldAlert, Shield, ShieldCheck } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';

interface SecurityWarningProps {
  status: 'pending' | 'safe' | 'flagged' | 'blocked';
  threats?: Array<{
    type: string;
    description?: string;
  }>;
  checkedAt?: string;
  showDetails?: boolean;
}

export function SecurityWarning({ 
  status, 
  threats = [], 
  checkedAt,
  showDetails = true 
}: SecurityWarningProps) {
  if (status === 'pending') {
    return (
      <Alert className="border-yellow-500/50 bg-yellow-50/50">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertTitle className="text-yellow-900">Security Check Pending</AlertTitle>
        <AlertDescription className="text-yellow-700">
          This QR code is being scanned for security threats. Check back in a moment.
        </AlertDescription>
      </Alert>
    );
  }

  if (status === 'safe') {
    return showDetails ? (
      <Alert className="border-green-500/50 bg-green-50/50">
        <ShieldCheck className="h-4 w-4 text-green-600" />
        <AlertTitle className="text-green-900">Verified Safe</AlertTitle>
        <AlertDescription className="text-green-700">
          This QR code has been scanned and no security threats were detected.
          {checkedAt && (
            <span className="text-xs block mt-1">
              Last checked: {new Date(checkedAt).toLocaleDateString()}
            </span>
          )}
        </AlertDescription>
      </Alert>
    ) : null;
  }

  if (status === 'flagged') {
    return (
      <Alert className="border-orange-500/50 bg-orange-50/50">
        <ShieldAlert className="h-4 w-4 text-orange-600" />
        <AlertTitle className="text-orange-900">Security Warning</AlertTitle>
        <AlertDescription className="text-orange-700">
          <p className="mb-2">
            This QR code contains potentially suspicious content. Please review carefully before sharing.
          </p>
          {threats.length > 0 && showDetails && (
            <div className="mt-2 space-y-1">
              <p className="font-medium text-sm">Detected Issues:</p>
              <div className="flex flex-wrap gap-1">
                {threats.map((threat, idx) => (
                  <Badge 
                    key={idx} 
                    variant="outline" 
                    className="border-orange-500/50 text-orange-700 text-xs"
                  >
                    {threat.type.replace(/_/g, ' ').toLowerCase()}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  if (status === 'blocked') {
    return (
      <Alert className="border-red-500/50 bg-red-50/50">
        <ShieldAlert className="h-4 w-4 text-red-600" />
        <AlertTitle className="text-red-900">Security Threat Detected</AlertTitle>
        <AlertDescription className="text-red-700">
          <p className="mb-2">
            <strong>This QR code has been blocked.</strong> It contains malicious content and cannot be used.
          </p>
          {threats.length > 0 && showDetails && (
            <div className="mt-2 space-y-1">
              <p className="font-medium text-sm">Detected Threats:</p>
              <div className="flex flex-wrap gap-1">
                {threats.map((threat, idx) => (
                  <Badge 
                    key={idx} 
                    variant="destructive" 
                    className="text-xs"
                  >
                    {threat.type.replace(/_/g, ' ').toLowerCase()}
                  </Badge>
                ))}
              </div>
              <p className="text-xs mt-2">
                Contact support if you believe this is an error.
              </p>
            </div>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  return null;
}

// Compact badge version for tables/lists
export function SecurityBadge({ status }: { status: string }) {
  const badgeConfig = {
    pending: { icon: Shield, label: 'Checking...', className: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
    safe: { icon: ShieldCheck, label: 'Safe', className: 'bg-green-100 text-green-700 border-green-300' },
    flagged: { icon: ShieldAlert, label: 'Warning', className: 'bg-orange-100 text-orange-700 border-orange-300' },
    blocked: { icon: ShieldAlert, label: 'Blocked', className: 'bg-red-100 text-red-700 border-red-300' },
  };

  const config = badgeConfig[status as keyof typeof badgeConfig] || badgeConfig.pending;
  const Icon = config.icon;

  return (
    <Badge variant="outline" className={`${config.className} text-xs flex items-center gap-1 w-fit`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}
